import React from "react";
import type { User } from "@src/services/api/tdApi";
import { authService } from "@src/services/api/tdApi";

interface UseAuthReturn {
  user: User | null;
  isAuthenticating: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Unified authentication hook
 * Provides a single interface for all authentication operations
 * Automatically checks authentication status on mount
 *
 * NOTE: This hook creates local state for each component that uses it.
 * Currently, we pass the login function down via props to ensure a single source of truth.
 * In the future, consider using AuthContext/Provider to avoid prop drilling:
 * <AuthProvider><App /></AuthProvider>
 */
export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = React.useState<User | null>(null);
  const [isAuthenticating, setIsAuthenticating] = React.useState(true); // Start as true
  const [error, setError] = React.useState<string | null>(null);

  const login = React.useCallback(async (email: string, password: string) => {
    setError(null);
    setIsAuthenticating(true);

    try {
      const userData = await authService.login(email, password);
      setUser(userData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Login failed. Please try again.";
      setError(errorMessage);
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  const logout = React.useCallback(async () => {
    setError(null);
    setIsAuthenticating(true);

    try {
      await authService.logout();
      setUser(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Logout failed. Please try again.";
      setError(errorMessage);
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  // Check authentication on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await authService.checkAuthentication();
        if (userData) {
          setUser(userData);
        }
        setIsAuthenticating(false);
      } catch (err) {
        console.error("Auth check failed:", err);
        setIsAuthenticating(false);
      }
    };

    checkAuth();
  }, []);

  return {
    user,
    isAuthenticating,
    error,
    login,
    logout,
  };
};
